import React,{useCallback,useEffect,useRef,useState}from'react';
import{Camera,CameraOff,Check,RefreshCw,ScanLine,X}from'@/components/icons';
import{Dialog,DialogContent,DialogHeader,DialogTitle}from'@/components/ui/dialog';
import{Button}from'@/components/ui/button';
import{toast}from'sonner';
import{captureVideoFrame,getCameraErrorMessage,openCameraStream,releaseCameraStream}from'@/lib/camera';

const BACKEND_URL=process.env.REACT_APP_BACKEND_URL;
const wait=ms=>new Promise(resolve=>setTimeout(resolve,ms));

async function getNativeQrDetector(){
 if(typeof window==='undefined'||!window.BarcodeDetector)return null;
 try{
  if(typeof window.BarcodeDetector.getSupportedFormats==='function'){
   const formats=await window.BarcodeDetector.getSupportedFormats();
   if(!formats.includes('qr_code'))return null;
  }
  return new window.BarcodeDetector({formats:['qr_code']});
 }catch{return null}
}

export default function LotScanner({open,onOpenChange,onResolved,title='Scansiona QR lotto',targetLabel='',continuous=false}){
 const videoRef=useRef(null),streamRef=useRef(null),timerRef=useRef(null),requestRef=useRef(0),cameraStartingRef=useRef(false),busyRef=useRef(false),frameBusyRef=useRef(false),lastRef=useRef({value:'',at:0}),nativeCameraInputRef=useRef(null),onResolvedRef=useRef(onResolved),onOpenChangeRef=useRef(onOpenChange),resolveCodeRef=useRef(null),wasOpenRef=useRef(false),sessionTargetRef=useRef(targetLabel);
 const[status,setStatus]=useState('Avvio fotocamera…'),[cameraError,setCameraError]=useState(''),[cameraReady,setCameraReady]=useState(false),[videoDevices,setVideoDevices]=useState([]),[activeDeviceId,setActiveDeviceId]=useState(''),[feedback,setFeedback]=useState('idle'),[sessionTitle,setSessionTitle]=useState(title),[sessionTarget,setSessionTarget]=useState(targetLabel);
 useEffect(()=>{onResolvedRef.current=onResolved;onOpenChangeRef.current=onOpenChange},[onResolved,onOpenChange]);
 useEffect(()=>{if(open&&!wasOpenRef.current){sessionTargetRef.current=targetLabel;setSessionTitle(title);setSessionTarget(targetLabel);setFeedback('idle')}wasOpenRef.current=open},[open,title,targetLabel]);

 const resolveCode=useCallback(async raw=>{
  const value=String(raw||'').trim();if(!value||busyRef.current)return false;
  const now=Date.now();if(lastRef.current.value===value&&now-lastRef.current.at<1800)return false;
  lastRef.current={value,at:now};busyRef.current=true;setFeedback('reading');setStatus('QR letto · verifico il lotto…');
  try{
   const r=await fetch(`${BACKEND_URL}/api/lots/resolve-qr`,{method:'POST',headers:{'Content-Type':'application/json'},credentials:'include',body:JSON.stringify({qr_code:value})});
   if(!r.ok){const e=await r.json().catch(()=>({detail:'Lotto non trovato'}));throw new Error(e.detail||'Lotto non trovato')}
   const lot=await r.json();
   const accepted=await onResolvedRef.current?.(lot.lot_id);
   if(accepted===false){setFeedback('error');setStatus(`QR letto · lotto ${lot.lot_number||''} non utilizzabile qui`);return false}
   setFeedback('success');setStatus(`Lotto ${lot.lot_number||''} riconosciuto e verificato`);
   if(!continuous){await wait(650);onOpenChangeRef.current?.(false)}else{await wait(450);setFeedback('idle');setStatus('Inquadra il prossimo QR')}
   return true;
  }catch(e){setFeedback('error');setStatus(e.message||'QR non riconosciuto');toast.error(e.message||'QR non riconosciuto');return false}
  finally{busyRef.current=false}
 },[continuous]);
 useEffect(()=>{resolveCodeRef.current=resolveCode},[resolveCode]);

 const stopCamera=useCallback(()=>{
  requestRef.current+=1;cameraStartingRef.current=false;
  if(timerRef.current)clearTimeout(timerRef.current);timerRef.current=null;
  releaseCameraStream(streamRef.current,videoRef.current);streamRef.current=null;
  frameBusyRef.current=false;busyRef.current=false;setCameraReady(false);
 },[]);

 const decodeImage=useCallback(async blob=>{
  if(!blob||frameBusyRef.current||busyRef.current)return false;
  frameBusyRef.current=true;
  try{
   const fd=new FormData();fd.append('image',blob,'qr-frame.jpg');
   const r=await fetch(`${BACKEND_URL}/api/lots/decode-qr-image`,{method:'POST',credentials:'include',body:fd});
   if(!r.ok)return false;
   const data=await r.json();
   if(!data.qr_code)return false;
   await resolveCodeRef.current?.(data.qr_code);
   return true;
  }catch{return false}
  finally{frameBusyRef.current=false}
 },[]);

 const startDecodeLoop=useCallback(detector=>{
  if(timerRef.current)clearTimeout(timerRef.current);
  const tick=async()=>{
   if(!streamRef.current)return;
   const video=videoRef.current;
   try{
    if(!busyRef.current&&video?.readyState>=2&&video.videoWidth){
     if(detector){const found=await detector.detect(video);const raw=found?.[0]?.rawValue;if(raw)await resolveCodeRef.current?.(raw)}
     else{const blob=await captureVideoFrame(video,{maxDimension:640,quality:0.72});await decodeImage(blob)}
    }
   }catch(_){ }
   if(streamRef.current)timerRef.current=setTimeout(tick,detector?280:650);
  };
  timerRef.current=setTimeout(tick,120);
 },[decodeImage]);

 const startCamera=useCallback(async({deviceId='',facingMode='environment'}={})=>{
  const videoElement=videoRef.current;if(!videoElement||cameraStartingRef.current)return;
  cameraStartingRef.current=true;
  const requestId=requestRef.current+1;requestRef.current=requestId;
  if(timerRef.current)clearTimeout(timerRef.current);timerRef.current=null;
  releaseCameraStream(streamRef.current,videoElement);streamRef.current=null;
  setCameraReady(false);setCameraError('');setFeedback('idle');setStatus('Avvio fotocamera…');
  try{
   const opened=await openCameraStream({videoElement,deviceId,facingMode,width:1280,height:720});
   if(requestId!==requestRef.current){releaseCameraStream(opened.stream,videoElement);return}
   streamRef.current=opened.stream;setActiveDeviceId(opened.deviceId);setVideoDevices(opened.devices);
   const detector=await getNativeQrDetector();
   if(requestId!==requestRef.current)return;
   setCameraReady(true);setStatus(sessionTargetRef.current?`Inquadra il QR di ${sessionTargetRef.current}`:'Inquadra il QR del lotto');startDecodeLoop(detector);
  }catch(error){
   if(requestId===requestRef.current){releaseCameraStream(streamRef.current,videoElement);streamRef.current=null;setCameraReady(false);setCameraError(getCameraErrorMessage(error,{fallbackLabel:'Usa la fotocamera del dispositivo.'}));setStatus('Fotocamera web non disponibile')}
  }finally{
   if(requestId===requestRef.current)cameraStartingRef.current=false;
  }
 },[startDecodeLoop]);

 const handleVideoRef=useCallback(node=>{
  videoRef.current=node;
  if(node&&open&&!streamRef.current)startCamera({facingMode:'environment'});
 },[open,startCamera]);

 useEffect(()=>{
  if(!open)return undefined;
  return()=>stopCamera();
 },[open,stopCamera]);

 const handleSwitchCamera=()=>{
  if(videoDevices.length<2)return;
  const currentIndex=videoDevices.findIndex(d=>d.deviceId===activeDeviceId);
  const nextIndex=currentIndex>=0?(currentIndex+1)%videoDevices.length:0;
  startCamera({deviceId:videoDevices[nextIndex].deviceId});
 };
 const handleNativeCameraChange=async e=>{
  const file=e.target.files?.[0];e.target.value='';if(!file)return;
  setFeedback('reading');setStatus('Lettura QR dalla foto…');
  const ok=await decodeImage(file);
  if(!ok){setFeedback('error');setStatus('QR non rilevato nella foto');toast.error('QR non rilevato. Riprova avvicinandoti all’etichetta.')}
 };
 const close=()=>onOpenChangeRef.current?.(false);
 const frameClass=feedback==='success'?'border-emerald-400':feedback==='error'?'border-red-400':feedback==='reading'?'border-sky-400':'border-white/90';

 return <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5"/>{sessionTitle}</DialogTitle></DialogHeader><div className="space-y-3">
  <input ref={nativeCameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleNativeCameraChange} className="hidden"/>
  <div className="relative overflow-hidden rounded-xl border bg-black aspect-[4/3] flex items-center justify-center"><video ref={handleVideoRef} autoPlay muted playsInline onCanPlay={()=>setCameraReady(true)} className="w-full h-full object-cover"/>{!cameraError&&<div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className={`relative aspect-square w-[58%] max-w-[250px] rounded-2xl border-2 ${frameClass} shadow-[0_0_0_999px_rgba(0,0,0,0.28)] transition-colors`}><span className={`absolute -left-1 -top-1 h-7 w-7 rounded-tl-xl border-l-4 border-t-4 ${frameClass}`}/><span className={`absolute -right-1 -top-1 h-7 w-7 rounded-tr-xl border-r-4 border-t-4 ${frameClass}`}/><span className={`absolute -bottom-1 -left-1 h-7 w-7 rounded-bl-xl border-b-4 border-l-4 ${frameClass}`}/><span className={`absolute -bottom-1 -right-1 h-7 w-7 rounded-br-xl border-b-4 border-r-4 ${frameClass}`}/></div></div>}{!cameraError&&!cameraReady&&<div className="absolute inset-0 flex items-center justify-center text-sm text-white bg-black/35">Avvio fotocamera…</div>}{feedback==='success'&&<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-emerald-950/55 text-white"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500"><Check className="h-9 w-9"/></div><p className="font-semibold">QR letto</p></div>}{feedback==='error'&&!cameraError&&<div className="pointer-events-none absolute left-3 right-3 bottom-3 flex items-center gap-2 rounded-lg bg-red-600/90 px-3 py-2 text-sm text-white"><X className="h-4 w-4 shrink-0"/><span>QR letto ma non valido per questo passaggio</span></div>}{cameraError&&<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-5 text-center text-white bg-black/75"><CameraOff className="h-8 w-8"/><p className="text-sm">{cameraError}</p></div>}</div>
  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${feedback==='success'?'border-emerald-200 bg-emerald-50 text-emerald-800':feedback==='error'?'border-red-200 bg-red-50 text-red-800':'border-slate-200 bg-slate-50 text-slate-700'}`}>{feedback==='success'?<Check className="h-4 w-4 shrink-0"/>:feedback==='error'?<X className="h-4 w-4 shrink-0"/>:<Camera className="h-4 w-4 shrink-0"/>}<span>{status}</span></div>
  {videoDevices.length>1&&!cameraError&&<Button type="button" variant="outline" className="w-full" onClick={handleSwitchCamera}><RefreshCw className="h-4 w-4"/>Cambia fotocamera</Button>}
  {cameraError&&<Button type="button" variant="outline" className="w-full" onClick={()=>nativeCameraInputRef.current?.click()}><Camera className="h-4 w-4"/>Usa fotocamera del dispositivo</Button>}
  <Button type="button" variant="ghost" className="w-full" onClick={close}>Chiudi e seleziona lotto manualmente</Button>
 </div></DialogContent></Dialog>;
}
